/**
 * React Query hook for Expense Types (Global)
 * Provides automatic caching, background sync, and error handling
 * Can be used across all modules
 */

import { useQuery } from '@tanstack/react-query';
import { listExpenseType } from 'services/billingServices';
import { totalCountEvent } from 'services/generalServices';
import { queryKeys } from '../config/queryClient';

/**
 * Fetches expense types data
 * Always loads the full list by getting total count first, then fetching all items
 * Same as CreateExpense implementation
 *
 * @param {number} pageLimit - Limit for fetching expense types
 * @param {string} pagination_token - Pagination token (default: '')
 * @param {string} pagination_direction - Pagination direction ('forward' or 'reverse') (default: 'forward')
 * @param {string} next - Next page token (default: '')
 * @param {string} filter - Filter string (default: '')
 * @param {boolean} excludeFromLoading - Exclude from loading state (default: false)
 * @returns {Promise<Array>} - Array of expense types
 */
const fetchExpenseTypes = async (
  pageLimit,
  pagination_token = '',
  pagination_direction = 'forward',
  next = '',
  filter = '',
  excludeFromLoading = false
) => {
  try {
    // Fetch expense types with all parameters
    // Note: filter is passed as first parameter to match Redux action signature
    const response = await listExpenseType(filter, pageLimit, pagination_token, pagination_direction, next);

    return response?.data?.['expense_type'] || [];
  } catch (error) {
    console.error('Error fetching expense types:', error);
    throw error;
  }
};

/**
 * Hook to fetch expense types with React Query
 * Always loads the full list by getting total count first (same as CreateExpense implementation)
 *
 * @param {Object} options - Configuration options
 * @param {number} options.limit - Number of items per page (default: uses total count)
 * @param {string} options.pagination_token - Pagination token (default: '')
 * @param {string} options.pagination_direction - Pagination direction (default: 'forward')
 * @param {string} options.next - Next page token (default: '')
 * @param {string} options.filter - Filter string (default: '')
 * @param {boolean} options.excludeFromLoading - Exclude from loading state (default: false)
 * @param {boolean} options.enabled - Enable/disable query (default: true)
 *
 * @returns {Object} - expenseTypeList, isExpenseTypeLoading, error, refetch, isFetching
 */
export const useExpenseTypesQuery = ({
  limit,
  pagination_token = '',
  pagination_direction = 'forward',
  next = '',
  filter = '',
  excludeFromLoading = false,
  enabled = true,
} = {}) => {
  // Get total count first (same as CreateExpense) - only if limit is not provided
  const { data: countData } = useQuery({
    queryKey: ['expense-type-count'],
    queryFn: async () => {
      const countResponse = await totalCountEvent('expense_type');
      return countResponse?.data || 10;
    },
    enabled: enabled && !limit, // Only fetch count if limit is not provided
    staleTime: 10 * 60 * 1000, // Consider data stale after 10 minutes
    gcTime: 15 * 60 * 1000, // Keep in cache for 15 minutes (gcTime replaces cacheTime in v4+)
    refetchOnWindowFocus: false, // Don't refetch on window focus for relatively static data
    retry: 2, // Retry twice on failure
  });

  const pageLimit = limit || countData || 10;

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: queryKeys.expenseTypes.list(pageLimit, pagination_token, pagination_direction, next, filter),
    queryFn: () => fetchExpenseTypes(pageLimit, pagination_token, pagination_direction, next, filter, excludeFromLoading),
    enabled: enabled && (limit || !!countData), // Only fetch when limit is provided or count is available
    staleTime: 10 * 60 * 1000, // Consider data stale after 10 minutes
    gcTime: 15 * 60 * 1000, // Keep in cache for 15 minutes (gcTime replaces cacheTime in v4+)
    refetchOnWindowFocus: false, // Don't refetch on window focus for relatively static data
    retry: 2, // Retry twice on failure
  });

  return {
    expenseTypeList: data || [],
    isExpenseTypeLoading: isLoading,
    isFetching,
    error,
    refetch,
  };
};
